import styleResolver, {SOURCE_RASTER} from 'Map/tier/styleParts/styleResolver';
import rasterTrafficFlowTiles from 'Map/tier/styleParts/rasterTrafficFlowTiles';
import {vectorTrafficTilesResolver} from 'Map/tier/styleParts/vectorStyleResolver';
import initializeStyleOption from 'Map/tier/styleParts/initializeStyleOption';
import periodicUpdater from 'Map/periodicUpdater';
import {trafficFlowUrlTransformer} from 'Map/tier/urlTransformers/urlTransformers';
import initializeOptions from 'Map/configurer/initializeTrafficFlowTilesOptions';
import updateLayersVisibility from 'Map/tier/styleUpdate/updateLayersVisibility';

export const ID = 'trafficFlowTiles';
const POSITION_TRAFFIC_FLOW_TILES = 2;

function getTrafficFlowTilesStyle(options) {
    const styleResolverFactory = (styleResolverOptions) =>
        styleResolver(
            styleResolverOptions.unwrapedTheme,
            () => rasterTrafficFlowTiles(styleResolverOptions.thickness, styleResolverOptions.unwrapedTheme.style),
            () => vectorTrafficTilesResolver(styleResolverOptions)
        );

    const styleResolverOptions = {
        theme: options.theme,
        key: options.key,
        thickness: options.thickness
    };

    return initializeStyleOption(styleResolverOptions, styleResolverFactory);
}

/**
 * This tier provides
 *  <a href="https://developer.tomtom.com/traffic-api/traffic-api-documentation/traffic-flow">Traffic Flow</a>
 *  information.
 *
 * ```javascript
 * var map = tt.map({
 *  //your map configuration
 *});
 * map.on('load', function() {
 *     map.addTier(new tt.TrafficFlowTilesTier({
 *         key: <api key>//your traffic flow api key goes here
 *         //or provide more detailed configuration
 *     }))
 * });
 *```
 *
 * @class TrafficFlowTilesTier
 * @namespace Maps
 */

export class TrafficFlowTilesTier {
    // eslint-disable
    /**
     * @method constructor
     *
     * @param {Object} options Traffic flow configuration options
     * @param {String} options.key Traffic flow api key
     * @param {String} [options.style='tomtom://vector/1/relative'] The map style.
     * It can be one of following types:
     * * Schema, you can use of following values:
     *   1. `tomtom://vector/1/relative`
     *   2. `tomtom://vector/1/absolute`
     *   3. `tomtom://vector/1/relative-delay`
     *
     *   4. `tomtom://raster/1/relative`
     *   5. `tomtom://raster/1/absolute`
     *   6. `tomtom://raster/1/relative-delay`
     * * URL to the JSON object conforming
     * to the schema described in the
     * <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification">
     * Map Style Specification</a>.
     * * JSON object conforming to the mentioned specification.
     * @param {Number} [options.refresh=30000] The interval in milliseconds between subsequent refreshes of traffic
     * flow information. Any value lower than 30000 will be treated as 30000
     */
    // eslint-disable
    constructor(options, position = POSITION_TRAFFIC_FLOW_TILES) {
        this.options = initializeOptions(options);
        this.position = position;
        this.style = null;
        this.updatePeriodicUpdater = true;
        this.urlTransformer = trafficFlowUrlTransformer(this.options.key, this.options.theme.getSource());
        this.map = null;
    }

    /**
     * @method getId
     * @description Returns tier's ID
     * @return {String}
     */
    getId = () => ID;
    getPosition = () => this.position;
    getStyle = () => this.style;
    setStyle = (style) => {
        this.style = style;
    };
    getOptions = () => this.options;

    _refresh = async (map) => {
        if (!this.isRaster()) {
            const sourceName = Object.keys(this.getStyle().sources)[0];
            map.__om.style.sourceCaches[sourceName].clearTiles();
        }
        updateLayersVisibility(map, this.getStyle().layers, 'none');
        updateLayersVisibility(map, this.getStyle().layers, 'visible');
    };

    onBeforeAdd = () => {
        this.style = getTrafficFlowTilesStyle(this.options);
    };

    onAdd = (map) => {
        periodicUpdater.set(map, ID, this.options.refresh, async () => {
            await this._refresh(map);
        });
        this.map = map;
        map.once('remove', this.onRemove);
    };

    onRemove = (map) => {
        if (!this.map) {
            this.map = map;
        }

        periodicUpdater.remove(this.map, ID);
        this.map.off('remove', this.onRemove);
    };

    getUrlTransformer = () => this.urlTransformer;

    isRaster = () => {
        return this.options.theme.getSource() === SOURCE_RASTER;
    };
}

export default (options, position) => {
    return new TrafficFlowTilesTier(options, position);
};



// WEBPACK FOOTER //
// ./map/tier/trafficFlowTilesTier.js